import { useTheme } from "@hooks/useTheme";
import TreeNode from "./TreeNode";
import {
  CYCLONEDX_STRUCTURE,
  SBOM_FORMAT_CYCLONEDX,
  SBOM_FORMAT_SPDX_2_3,
  SBOM_FORMAT_SPDX_3,
  SPDX_23_STRUCTURE,
  SPDX_STRUCTURE,
} from "@constants/sbom";
import { useMemo, useRef, useState } from "react";
import { InformationIcon } from "@components/Icons";
import TableForUIPagination from "@components/Tables/TableForUIPagination";
import { useTranslation } from "react-i18next";
import { useOnClickOutside } from "@hooks/useOnClickOutside";

const SbomInfoPopup = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);

  const wrapperRef = useRef()
  useOnClickOutside(wrapperRef, () => setOpen(false));

  const columns = ["symbol", "description"]
  const columnConfig = {
    symbol: {
      headerRender: () => (
        <div className="text-center">{t("sbom.sbom-infor-model.symbol", "Symbol")}</div>
      ),
      customRender: (value) => (
        <div className="text-center">{value}</div>
      )
    },
    description: {
      headerRender: () => t("sbom.sbom-infor-model.description", "Description"),
    }
  }
  const rows = useMemo(() => {
    return [
      {
        symbol: "*",
        description: t("sbom.sbom-infor-model.requirement", "requirement")
      },
      {
        symbol: "?",
        description: t("sbom.sbom-infor-model.optional", "optional")
      }
    ]
  }, [t]);

  return (
    <div className="relative inline-block" data-testid="sbom-preview-info-popup">
      {/* Button info */}
      <button data-testid="sbom-preview-info-toggle-button" onClick={() => setOpen(!open)}>
        <InformationIcon size="20" className={`${colors.textInfoAccent} ${colors.hoverTextInfoAccent}`} />
      </button>

      {/* Popup */}
      {open && (
        <div ref={wrapperRef} className={`absolute right-full mr-2 top-0 w-72 rounded-xl ${colors.bgSbomInfoPanel} border ${colors.borderMuted} shadow-2xl z-50`} data-testid="sbom-preview-info-panel">
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-2 border-b ${colors.borderMuted}`}>
            <h3 className={`text-sm font-semibold uppercase ${colors.textPrimary}`}>
              {t("sbom.sbom-infor-model.title")}
            </h3>
            <button
              data-testid="sbom-preview-info-close-button"
              onClick={() => setOpen(false)}
              className={`${colors.textNeutralMuted} ${colors.hoverTextNeutralStrong}`}
            >
              ✕
            </button>
          </div>

          {/* Table */}
          <div className="px-4 py-4 flex-1 overflow-auto" data-testid="sbom-preview-legend-table">
            <TableForUIPagination
              columns={columns}
              columnConfig={columnConfig}
              data={rows}
              loading={false}
              currentPage={1}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SbomPreview = ({ format }) => {
  const { colors } = useTheme();

  const structures = useMemo(() => {
    switch (format) {
      case SBOM_FORMAT_SPDX_2_3:
        return SPDX_23_STRUCTURE;
      case SBOM_FORMAT_SPDX_3:
        return SPDX_STRUCTURE;
      case SBOM_FORMAT_CYCLONEDX:
      default:
        return CYCLONEDX_STRUCTURE;
    }
  }, [format]);

  return (
    <div className={`border rounded-lg ${colors.borderPrimary} ${colors.bgInput} p-4`} data-testid="sbom-preview">
      {/* Header + info popup */}
      <div className="flex justify-end mb-2">
        <SbomInfoPopup />
      </div>

      {/* Grid render tree */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="sbom-preview-tree">
        {structures.map((s, idx) => (
          <TreeNode key={idx} node={s} isRoot />
        ))}
      </div>
    </div>
  );
};


export default SbomPreview;